import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import {
  Stack,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import type { Chart } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { ActivityDataTable } from './ActivityDataTable';
import {
  aggregateDataByDate,
  getDefaultAggregation,
  getAvailableAggregations,
  AggregationLevel,
} from '../utils/dateAggregation';
import { STATISTICS_CONSTANTS } from '../constants/statistics.constants';
import { createSearchChartOptions } from '../utils/chartOptions';
import { exportCSV } from '../utils/csvExport';
import { Dayjs } from 'dayjs';
import { getPatternForColor } from '../utils/chartPatterns';
import { useZoomDetection } from '../hooks/useZoomDetection';
import { useScreenReaderDetection } from '../hooks/useScreenReaderDetection';
import { ViewModeToggle } from './ViewModeToggle';

interface MainActivityChartProps {
  data: any[] | undefined;
  isLoading: boolean;
  startDate: Dayjs;
  endDate: Dayjs;
  aggregationLevel: AggregationLevel | undefined;
  onAggregationChange: (level: AggregationLevel) => void;
  isMobile: boolean;
}

const AGGREGATION_ICONS = {
  day: CalendarViewDayIcon,
  week: CalendarViewWeekIcon,
  month: CalendarViewMonthIcon,
};

export const MainActivityChart: React.FC<MainActivityChartProps> = ({
  data,
  startDate,
  endDate,
  aggregationLevel,
  onAggregationChange,
  isMobile,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isVeryCompact } = useZoomDetection();
  const isScreenReader = useScreenReaderDetection();
  const [viewMode, setViewMode] = useState<'chart' | 'table'>(
    isVeryCompact || isScreenReader ? 'table' : 'chart'
  );
  const chartRef = useRef<Chart<'bar', number[], string> | null>(null);
  const [legendVisible, setLegendVisible] = useState([true, true]);

  // Hack: Check high DPI screens for adjusted chart height
  const isHighDPI = useMemo(() => {
    const baseRatio = parseFloat(localStorage.getItem('basePixelRatio') || '1');
    return baseRatio > 1.5;
  }, []);

  useEffect(() => {
    setViewMode(isVeryCompact || isScreenReader ? 'table' : 'chart');
  }, [isVeryCompact, isScreenReader]);

  const effectiveAggregation = useMemo(
    () => aggregationLevel || getDefaultAggregation(startDate, endDate),
    [aggregationLevel, startDate, endDate]
  );

  const availableAggregations = useMemo(
    () => getAvailableAggregations(startDate, endDate),
    [startDate, endDate]
  );

  const {
    aggregatedSearchData,
    aggregatedClickData,
    searchTotal,
    clicksTotal,
  } = useMemo(() => {
    if (!data?.length) {
      return {
        aggregatedSearchData: [],
        aggregatedClickData: [],
        searchTotal: 0,
        clicksTotal: 0,
      };
    }

    const searchEvents: Array<{ date: string; count: number }> = [];
    const clickEvents: Array<{ date: string; count: number }> = [];
    let searchSum = 0;
    let clickSum = 0;

    for (const item of data) {
      if (item.event === 'search') {
        searchEvents.push(item);
        searchSum += item.count;
      } else if (item.event === 'journal') {
        clickEvents.push(item);
        clickSum += item.count;
      }
    }

    return {
      aggregatedSearchData: aggregateDataByDate(
        searchEvents,
        effectiveAggregation as AggregationLevel,
        startDate,
        endDate
      ),
      aggregatedClickData: aggregateDataByDate(
        clickEvents,
        effectiveAggregation as AggregationLevel,
        startDate,
        endDate
      ),
      searchTotal: searchSum,
      clicksTotal: clickSum,
    };
  }, [data, effectiveAggregation, startDate, endDate]);

  const searchChart = useMemo(() => {
    if (!aggregatedSearchData.length && !aggregatedClickData.length) {
      return { labels: [], datasets: [] };
    }

    const labels = aggregatedSearchData.map((item) => item.label);
    const isLargeDataset =
      labels.length > STATISTICS_CONSTANTS.LARGE_DATASET_THRESHOLD;

    return {
      labels,
      datasets: [
        {
          label: `${t(
            'statistics.legend.searches',
            'Searches'
          )} (${searchTotal.toLocaleString()})`,
          data: aggregatedSearchData.map((item) => item.count),
          backgroundColor: theme.palette.primary.main,
          borderWidth: 0,
          stack: 'combined',
          barThickness: isLargeDataset ? ('flex' as const) : undefined,
          maxBarThickness: isLargeDataset
            ? Math.max(2, Math.floor(600 / labels.length))
            : undefined,
        },
        {
          label: `${t(
            'statistics.legend.journalClicks',
            'Clicks'
          )} (${clicksTotal.toLocaleString()})`,
          data: aggregatedClickData.map((item) => item.count),
          backgroundColor: getPatternForColor(
            theme.palette.warning.main,
            1,
            true
          ),
          borderWidth: 0,
          stack: 'combined',
          barThickness: isLargeDataset ? ('flex' as const) : undefined,
          maxBarThickness: isLargeDataset
            ? Math.max(2, Math.floor(600 / labels.length))
            : undefined,
        },
      ],
    };
  }, [
    aggregatedSearchData,
    aggregatedClickData,
    searchTotal,
    clicksTotal,
    theme,
    t,
  ]);

  const searchChartOptions = useMemo(() => {
    const base = createSearchChartOptions(theme);
    return {
      ...base,
      plugins: {
        ...base.plugins,
        legend: { ...base.plugins?.legend, display: false },
      },
    };
  }, [theme]);

  const toggleLegendItem = useCallback((datasetIndex: number) => {
    const chart = chartRef.current;
    if (!chart) return;
    const meta = chart.getDatasetMeta(datasetIndex);
    meta.hidden = !meta.hidden;
    chart.update();
    setLegendVisible((prev) => {
      const next = [...prev];
      next[datasetIndex] = !meta.hidden;
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    const csvData = [
      [
        t('statistics.csvData.period', 'Period'),
        t('statistics.legend.searches', 'Searches'),
        t('statistics.legend.journalClicks', 'Clicks'),
        t('statistics.csvData.total', 'Total'),
      ],
    ];

    aggregatedSearchData.forEach((item, index) => {
      const clickCount = aggregatedClickData[index]?.count || 0;
      csvData.push([
        item.label,
        String(item.count),
        String(clickCount),
        String(item.count + clickCount),
      ]);
    });

    exportCSV(
      csvData,
      t('statistics.csvData.activitySummary', 'Activity Summary')
    );
  };

  const getAccessibleDescription = () => {
    if (!aggregatedSearchData.length) return '';
    return aggregatedSearchData
      .map((item, index) => {
        const clickCount = aggregatedClickData[index]?.count || 0;
        return (
          `${item.label}: ${t(
            'statistics.legend.searches',
            'Searches'
          )} ${item.count.toLocaleString()}, ` +
          `${t(
            'statistics.legend.journalClicks',
            'Clicks'
          )} ${clickCount.toLocaleString()}`
        );
      })
      .join('. ');
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={8}
      >
        <ToggleButtonGroup
          value={effectiveAggregation}
          exclusive
          onChange={(e, newValue) => newValue && onAggregationChange(newValue)}
          size="small"
          aria-label={String(
            t('statistics.aggregation.ariaLabel', 'Data aggregation level')
          )}
        >
          {availableAggregations.map((level) => {
            const Icon =
              AGGREGATION_ICONS[level as keyof typeof AGGREGATION_ICONS];
            return (
              <ToggleButton
                key={level}
                value={level}
                aria-label={String(t(`statistics.aggregation.${level}`, level))}
                size="small"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {Icon && <Icon sx={{ fontSize: 16 }} />}
                  <span style={{ fontSize: 12 }}>
                    {t(
                      `statistics.aggregation.${level}`,
                      level.charAt(0).toUpperCase() + level.slice(1)
                    )}
                  </span>
                </Box>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" component="h2">
            {t('statistics.totalGraph.title')}
          </Typography>
        </Box>

        <ViewModeToggle
          value={viewMode}
          onChange={(mode) => setViewMode(mode)}
          onDownload={handleExportCSV}
          size="small"
        />
      </Stack>

      {viewMode === 'table' ? (
        <ActivityDataTable
          aggregatedSearchData={aggregatedSearchData}
          aggregatedClickData={aggregatedClickData}
          searchTotal={searchTotal}
          clicksTotal={clicksTotal}
        />
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              height: isMobile
                ? STATISTICS_CONSTANTS.MOBILE_CHART_HEIGHT
                : isHighDPI
                  ? 320
                  : STATISTICS_CONSTANTS.DESKTOP_CHART_HEIGHT,
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              role="img"
              aria-label={String(t('statistics.chart.activityAriaLabel'))}
              style={{
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              <Bar
                ref={chartRef}
                data={searchChart}
                options={searchChartOptions}
                aria-hidden="true"
              />
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                <p>
                  {t('statistics.chart.description', { type: 'activity' })}.
                </p>
                <p>
                  {t('statistics.totalGraph.title')}:{' '}
                  {searchTotal.toLocaleString()}{' '}
                  {t('statistics.legend.searches', 'searches').toLowerCase()},{' '}
                  {clicksTotal.toLocaleString()}{' '}
                  {t('statistics.legend.journalClicks', 'clicks').toLowerCase()}
                  .
                </p>
                <p>{getAccessibleDescription()}</p>
              </div>
            </div>
          </Box>
          {searchChart.datasets.length >= 2 && (
            <Box
              component="div"
              role="group"
              aria-label={String(
                t(
                  'statistics.legend.toggleAriaLabel',
                  'Toggle series visibility'
                )
              )}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mt: 1.5,
                padding: '10px 0',
              }}
            >
              <Box
                component="button"
                type="button"
                role="button"
                tabIndex={0}
                aria-pressed={legendVisible[0]}
                aria-label={String(
                  t(
                    'statistics.legend.searchesToggle',
                    'Searches, click to show or hide'
                  )
                )}
                onClick={() => toggleLegendItem(0)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleLegendItem(0);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '4px 10px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: theme.palette.text.secondary,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  opacity: legendVisible[0] ? 1 : 0.5,
                  '&:focus-visible': {
                    outline: '2px solid ' + theme.palette.primary.main,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 12,
                    borderRadius: 0,
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
                <span>
                  {t('statistics.legend.searches', 'Searches')} (
                  {searchTotal.toLocaleString()})
                </span>
              </Box>
              <Box
                component="button"
                type="button"
                role="button"
                tabIndex={0}
                aria-pressed={legendVisible[1]}
                aria-label={String(
                  t(
                    'statistics.legend.clicksToggle',
                    'Clicks, click to show or hide'
                  )
                )}
                onClick={() => toggleLegendItem(1)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleLegendItem(1);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '4px 10px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: theme.palette.text.secondary,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  opacity: legendVisible[1] ? 1 : 0.5,
                  '&:focus-visible': {
                    outline: '2px solid ' + theme.palette.primary.main,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 12,
                    borderRadius: 0,
                    backgroundColor: theme.palette.warning.main,
                  }}
                />
                <span>
                  {t('statistics.legend.journalClicks', 'Clicks')} (
                  {clicksTotal.toLocaleString()})
                </span>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
